import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";
import moment from "moment-timezone";
import Field from "../../../../../../../../components/shared/Form/Field";
import CustomTimeInput from "../../../../../../../../components/shared/inputs/CustomTimeInput";
import LocationSelect from "../components/LocationSelect";
import JobSelect from "../components/JobSelect";
import PayCodeSelect from "../components/PayCodeSelect";
import LevelSelect from "../components/LevelSelect";
import QualificationSelect from "../components/QualificationSelect";
import { useScheduleContext } from "../../../../../hooks/context/useScheduleContext";
import { useGroups } from "../../../../../../../Settings/General/OrganisationLevelGroups/context";
import { useQualificationPreferences } from "../../../../../hooks/useQualificationPreferences";
import { useRowGroupBy } from "../../../../../hooks/form/useRowGroupBy";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../../../../utils/helpers/date";

function Simple() {
    const { t } = useTranslation();
    const { allocatedGroups } = useGroups();
    const { selectedCellOrEvent } = useScheduleContext();
    const { control } = useFormContext();
    const qualifications = useQualificationPreferences();
    const rowGroupBy = useRowGroupBy();
    const [date, start, end] = useWatch({ control, name: ["date", "startDate", "endDate"] });

    const duration: number = useMemo(() => {
        const _date = selectedCellOrEvent?.isTemplate ? moment().format("YYYY-MM-DD") : date;
        const _start = combineDateAndTime(moment(_date), moment(start));
        let _end = combineDateAndTime(moment(_date), moment(end));
        if (moment(_end).isBefore(moment(_start), "seconds")) {
            _end = moment(_end).add(1, "day");
        }
        if (start && end) {
            return moment(_end).diff(moment(_start), "seconds");
        }
        return 0;
    }, [start, end, date, selectedCellOrEvent?.isTemplate]);

    return (
        <>
            <div className="tw-flex tw-items-start tw-gap-9">
                <Field
                    name="startDate"
                    label={t("start")}
                    layout="row"
                    deps={["endDate"]}
                    shouldUnregister={false}
                >
                    <CustomTimeInput placeholder={t("start")} />
                </Field>

                <div className="tw-flex tw-items-start tw-gap-9">
                    <Field
                        name="endDate"
                        label={t("end")}
                        layout="row"
                        deps={["startDate"]}
                        className="tw-grid-cols-[50px_1fr]"
                        shouldUnregister={false}
                    >
                        <CustomTimeInput placeholder={t("end")} />
                    </Field>
                    <div className="tw-text-sm tw-font-semibold tw-mb-4 tw-pt-2">
                        {renderDurationAsFormat(duration, "HH:mm")} {t("hours")}
                    </div>
                </div>
            </div>

            <Field
                name="payCode"
                label={t("pay-code")}
                layout="row"
                shouldUnregister={false}
            >
                <PayCodeSelect placeholder={t("pay-code")} filterName="payCode" />
            </Field>

            <Field
                name="job"
                label={t("job")}
                layout="row"
                shouldUnregister={false}
            >
                <JobSelect disabled={rowGroupBy?.field === "job"} />
            </Field>

            <Field
                name="location"
                label={t("location")}
                layout="row"
                shouldUnregister={false}
            >
                <LocationSelect disabled={rowGroupBy?.field === "location"} />
            </Field>

            {allocatedGroups?.map((group: any) => (
                <Field
                    key={group.id}
                    name={group.level.replaceAll("_", "")}
                    label={group.description}
                    layout="row"
                    shouldUnregister={false}
                >
                    <LevelSelect
                        group={group.id}
                        allowClear={!group.isRequired}
                        filterName={group.level.replaceAll("_", "")}
                        disabled={rowGroupBy?.field === group.level.replaceAll("_", "")}
                    />
                </Field>
            ))}

            {qualifications.skills && (
                <Field
                    name="skills"
                    label={t("skills")}
                    layout="row"
                >
                    <QualificationSelect field="skills" />
                </Field>
            )}

            {qualifications.licenses && (
                <Field
                    name="licenses"
                    label={t("licenses")}
                    layout="row"
                >
                    <QualificationSelect field="licenses" />
                </Field>
            )}

            {qualifications.certifications && (
                <Field
                    name="certifications"
                    label={t("certifications")}
                    layout="row"
                >
                    <QualificationSelect field="certifications" />
                </Field>
            )}
        </>
    );
}

export default Simple;
